import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Loader2,
  Check,
  CheckCircle2,
  AlertTriangle,
  X,
  Database,
  DollarSign,
  Save,
  History,
  CreditCard,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Edit,
  XCircle,
  Copy,
  Info,
  CheckCheck,
  HelpCircle,
  FileText,
  RefreshCw,
  MoreVertical,
  StickyNote,
  Filter,
  Search,
  AlertCircle,
  MoreHorizontal,
  Lock,
  Unlock,
  Calendar,
  Users,
  Banknote,
  ArrowUpDown
} from 'lucide-react';
import { format } from 'date-fns';
import OverpaymentWarning from '../components/payments/OverpaymentWarning';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

// ═══════════════════════════════════════════════════════════════════
// EXTRACTED COMPONENTS - Imported from components/payment-verification
// ═══════════════════════════════════════════════════════════════════
import {
  FieldValue,
  MasterAccountBadge,
  OpsApprovalCheckmark,
  BillCopyViewer,
  FAQInfoPopup
} from '../components/payment-verification';


// ═══════════════════════════════════════════════════════════════════
// INLINE COMPONENTS - Complex state dependencies, kept in main file
// ═══════════════════════════════════════════════════════════════════

// PAYMENT POPOVER CONTENT - Shows payment details OR add payment form
// ═══════════════════════════════════════════════════════════════════

const PaymentPopoverContent = ({ billId, amount, isPaid, paymentDetails, onSubmit, loading, orgUsers = [], currentUser, newCharges, totalAmountDue }) => {
  const [isEditing, setIsEditing] = useState(!isPaid);
  const [overpayConfirmed, setOverpayConfirmed] = useState(false);
  const [paymentData, setPaymentData] = useState({
    transaction_id: paymentDetails?.transaction_reference || '',
    requested_amount: amount || 0,
    amount_paid: paymentDetails?.amount_paid || amount || 0,
    transaction_fee: paymentDetails?.transaction_fee || 0,
    payment_method: paymentDetails?.payment_type || 'ACH',
    payment_date: paymentDetails?.payment_date || format(new Date(), 'yyyy-MM-dd'),
    paid_by: currentUser?.id || '',
    paid_by_name: paymentDetails?.paid_by_name || currentUser?.name || '',
    notes: paymentDetails?.notes || '',
  });

  // Calculate variance
  const variance = paymentData.requested_amount - paymentData.amount_paid;
  const hasVariance = Math.abs(variance) > 0.01;
  const isOverTolerance = Math.abs(variance) > 10;

  // Overpayment detection: if this is a second+ transaction, check total vs charges
  const existingPaidAmount = isPaid ? (paymentDetails?.amount_paid || 0) : 0;
  const newTotal = existingPaidAmount + paymentData.amount_paid;
  const refNewCharges = newCharges || 0;
  const refTotalDue = totalAmountDue || amount || 0;
  const isOverpay = isPaid && paymentData.amount_paid > 0 && newTotal > refTotalDue && newTotal > refNewCharges && Math.abs(newTotal - refTotalDue) > 0.01 && Math.abs(newTotal - refNewCharges) > 0.01;

  const handleUserChange = (userId) => {
    const selectedUser = orgUsers.find(u => u.id === userId);
    setPaymentData(p => ({ 
      ...p, 
      paid_by: userId,
      paid_by_name: selectedUser?.name || ''
    }));
  };

  const handleSubmit = () => {
    onSubmit({ 
      bill_id: billId, 
      transaction_reference: paymentData.transaction_id,
      payment_date: paymentData.payment_date,
      requested_amount: paymentData.requested_amount,
      amount_paid: paymentData.amount_paid,
      transaction_fee: paymentData.transaction_fee,
      payment_type: paymentData.payment_method,
      paid_by: paymentData.paid_by,
      paid_by_name: paymentData.paid_by_name,
      notes: paymentData.notes,
      overpaid: isOverpay && overpayConfirmed,
    });
  };

  // View mode - show payment details
  if (isPaid && !isEditing) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Payment Recorded
          </h4>
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7 text-xs">
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
        </div>
        <div className="space-y-2 text-sm">
          {paymentDetails?.engine_payment_status && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source:</span>
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">Engine</Badge>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Amount Paid:</span>
            <span className="font-semibold text-emerald-700">${Number(paymentDetails?.amount_paid || 0).toFixed(2)}</span>
          </div>
          {paymentDetails?.transaction_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Transaction Fee:</span>
              <span>${Number(paymentDetails.transaction_fee).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date:</span>
            <span>{paymentDetails?.payment_date || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Reference:</span>
            <span className="font-mono text-xs">{paymentDetails?.transaction_reference || '-'}</span>
          </div>
          {paymentDetails?.transaction_type && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span>{paymentDetails.transaction_type}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Method:</span>
            <span>{paymentDetails?.payment_type || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Paid By:</span>
            <span>{paymentDetails?.paid_by_name || '-'}</span>
          </div>
        </div>
      </div>
    );
  }

  // Edit/Add mode - show payment form
  return (
    <div className="p-4 space-y-3">
      <h4 className="font-semibold text-sm flex items-center gap-2">
        <CreditCard className="h-4 w-4" />
        {isPaid ? 'Edit Payment' : 'Record Payment'}
      </h4>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Transaction ID</Label>
            <Input
              value={paymentData.transaction_id}
              onChange={(e) => setPaymentData(p => ({ ...p, transaction_id: e.target.value }))}
              placeholder="TXN-123456"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Payment Date</Label>
            <Input
              type="date"
              value={paymentData.payment_date}
              onChange={(e) => setPaymentData(p => ({ ...p, payment_date: e.target.value }))}
              className="h-8 text-xs"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Requested</Label>
            <Input
              type="number"
              step="0.01"
              value={paymentData.requested_amount}
              className="h-8 text-xs bg-muted"
              readOnly
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Amount Paid</Label>
            <Input
              type="number"
              step="0.01"
              value={paymentData.amount_paid}
              onChange={(e) => setPaymentData(p => ({ ...p, amount_paid: parseFloat(e.target.value) || 0 }))}
              className={`h-8 text-xs ${hasVariance ? 'border-amber-400' : ''}`}
              data-testid="pfv2-amount-paid"
            />
            <OverpaymentWarning
              paidAmount={paymentData.amount_paid}
              requestedAmount={paymentData.requested_amount}
              testId="pfv2-overpay-warning"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Transaction Fee</Label>
            <Input
              type="number"
              step="0.01"
              value={paymentData.transaction_fee}
              onChange={(e) => setPaymentData(p => ({ ...p, transaction_fee: parseFloat(e.target.value) || 0 }))}
              className="h-8 text-xs"
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Method</Label>
            <Select 
              value={paymentData.payment_method} 
              onValueChange={(v) => setPaymentData(p => ({ ...p, payment_method: v }))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACH">ACH</SelectItem>
                <SelectItem value="Wire">Wire</SelectItem>
                <SelectItem value="Cheque">Cheque</SelectItem>
                <SelectItem value="Credit Card">Credit Card</SelectItem>
                <SelectItem value="Account Pay">Account Pay</SelectItem>
                <SelectItem value="Auto Pay">Auto Pay</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Paid By</Label>
          <Select value={paymentData.paid_by} onValueChange={handleUserChange}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              {orgUsers.length > 0 ? (
                orgUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} {user.id === currentUser?.id && '(You)'}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value={currentUser?.id || ''}>
                  {currentUser?.name || 'Current User'}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Notes field */}
        <div className="space-y-1">
          <Label className="text-xs">Notes (Optional)</Label>
          <Textarea
            value={paymentData.notes}
            onChange={(e) => setPaymentData(p => ({ ...p, notes: e.target.value }))}
            placeholder="Payment notes..."
            className="h-16 text-xs resize-none"
          />
        </div>

        {/* Variance warning */}
        {hasVariance && !isOverpay && (
          <div className={`p-2 rounded text-xs ${isOverTolerance ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`}>
            Difference: ${Math.abs(variance).toFixed(2)}
            {isOverTolerance && ' - Consider adding to transaction fee'}
          </div>
        )}

        {/* Overpayment warning */}
        {isOverpay && (
          <div className="p-2 rounded text-xs bg-red-50 border border-red-200 text-red-800 space-y-1.5" data-testid="overpay-warning">
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" />
              Overpayment Warning
            </div>
            <p>Total paid (${existingPaidAmount.toFixed(2)} existing + ${paymentData.amount_paid.toFixed(2)} new = <strong>${newTotal.toFixed(2)}</strong>) exceeds:</p>
            <ul className="list-disc list-inside text-[10px] space-y-0.5">
              {refNewCharges > 0 && <li>New Charges: ${refNewCharges.toFixed(2)}</li>}
              <li>Total Amount Due: ${refTotalDue.toFixed(2)}</li>
            </ul>
            <p className="font-medium">You could be overpaying this vendor.</p>
            <label className="flex items-center gap-1.5 pt-1 cursor-pointer">
              <input type="checkbox" checked={overpayConfirmed} onChange={(e) => setOverpayConfirmed(e.target.checked)} className="rounded h-3 w-3" />
              <span>I confirm this overpayment is intentional</span>
            </label>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          {isPaid && (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} className="flex-1 h-8 text-xs">
              Cancel
            </Button>
          )}
          <Button 
            size="sm" 
            onClick={handleSubmit} 
            disabled={loading || !paymentData.transaction_id || (isOverpay && !overpayConfirmed)}
            className={`flex-1 h-8 text-xs ${isOverpay && overpayConfirmed ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
          >
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
            {isOverpay && overpayConfirmed ? 'Record (Overpaid)' : isPaid ? 'Update' : 'Record Payment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// FUNDS RECEIVED DIALOG - Add funds received entry
// ═══════════════════════════════════════════════════════════════════

const FundsReceivedDialog = ({ batchId, onSubmit, loading, open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    received_date: format(new Date(), 'yyyy-MM-dd'),
    amount: 0
  });

  const handleSubmit = () => {
    onSubmit(formData);
    setFormData({ received_date: format(new Date(), 'yyyy-MM-dd'), amount: 0 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            Add Funds Received
          </DialogTitle>
          <DialogDescription>
            Record funds received for this payment file
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="received_date">Received Date</Label>
            <Input
              id="received_date"
              type="date"
              value={formData.received_date}
              onChange={(e) => setFormData(p => ({ ...p, received_date: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
              placeholder="0.00"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || formData.amount <= 0}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Add Entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// AUDIT HISTORY SHEET
// ═══════════════════════════════════════════════════════════════════

const AuditHistorySheet = ({ rowId, invoiceId, accountId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!invoiceId || !accountId) return;
    setLoading(true);
    try {
      const response = await api.get(`/payment-verification/v2/invoices/${invoiceId}/audit?account_id=${accountId}`);
      setHistory(response.data.history || []);
    } catch (e) {
      console.error('Failed to fetch audit history:', e);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, accountId]);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, fetchHistory]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6" title="View History">
          <History className="h-3 w-3" />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Audit History</SheetTitle>
          <SheetDescription>Row #{rowId} change history</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No changes recorded</p>
          ) : (
            history.map((entry, i) => (
              <div key={entry.audit_id || i} className="border-l-2 border-primary pl-4 py-2">
                <p className="text-xs text-muted-foreground">
                  {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a') : '-'}
                </p>
                <p className="text-sm font-medium">
                  {entry.action === 'update' ? 'Updated' : entry.action} by {entry.user_name || 'Unknown'}
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">{entry.field}:</span>{' '}
                  <span className="line-through text-red-500">{entry.old_value || '-'}</span>{' → '}
                  <span className="text-emerald-600 font-medium">{entry.new_value || '-'}</span>
                </p>
                {entry.comment && (
                  <p className="text-xs text-muted-foreground mt-1 italic">"{entry.comment}"</p>
                )}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ═══════════════════════════════════════════════════════════════════
// INVOICE NOTE POPOVER - Add/Edit notes for invoice rows
// ═══════════════════════════════════════════════════════════════════

const InvoiceNotePopover = ({ invoiceId, accountId, hasNote, noteCount, user, onNoteChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const fetchNotes = useCallback(async () => {
    if (!invoiceId || !accountId) return;
    setLoading(true);
    try {
      const response = await api.get(`/payment-verification/v2/invoices/${invoiceId}/notes?account_id=${accountId}`);
      setNotes(response.data.notes || []);
    } catch (e) {
      console.error('Failed to fetch notes:', e);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, accountId]);

  useEffect(() => {
    if (open) {
      fetchNotes();
    }
  }, [open, fetchNotes]);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setSaving(true);
    try {
      await api.post(
        `/payment-verification/v2/invoices/${invoiceId}/notes?account_id=${accountId}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        { content: newNoteContent }
      );
      setNewNoteContent('');
      fetchNotes();
      onNoteChange?.(invoiceId, true);
      toast.success('Note added');
    } catch (e) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      await api.put(
        `/payment-verification/v2/invoices/${invoiceId}/notes/${noteId}?account_id=${accountId}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        { content: editContent }
      );
      setEditingNoteId(null);
      setEditContent('');
      fetchNotes();
      toast.success('Note updated');
    } catch (e) {
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/payment-verification/v2/invoices/${invoiceId}/notes/${noteId}?account_id=${accountId}`);
      fetchNotes();
      if (notes.length <= 1) {
        onNoteChange?.(invoiceId, false);
      }
      toast.success('Note deleted');
    } catch (e) {
      toast.error('Failed to delete note');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className={`p-1 rounded transition-colors relative ${hasNote ? 'text-blue-600 hover:text-blue-700' : 'text-gray-400 hover:text-gray-600'}`}
          title={hasNote ? `${noteCount || 1} note(s)` : 'Add note'}
        >
          <StickyNote className="h-4 w-4" />
          {hasNote && (
            <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-600 rounded-full" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Notes</h4>
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
          </div>
          
          {/* Existing Notes */}
          <div className="max-h-48 overflow-y-auto space-y-2">
            {notes.length === 0 && !loading && (
              <p className="text-xs text-muted-foreground text-center py-2">No notes yet</p>
            )}
            {notes.map(note => (
              <div key={note.note_id} className="bg-muted/50 rounded p-2 text-xs">
                {editingNoteId === note.note_id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="text-xs min-h-[60px]"
                    />
                    <div className="flex gap-1">
                      <Button size="sm" className="h-6 text-xs" onClick={() => handleUpdateNote(note.note_id)} disabled={saving}>
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditingNoteId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center justify-between mt-1 text-muted-foreground">
                      <span>{note.created_by_name} • {note.created_at ? format(new Date(note.created_at), 'MMM d, h:mm a') : ''}</span>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingNoteId(note.note_id); setEditContent(note.content); }} className="hover:text-primary">
                          <Edit className="h-3 w-3" />
                        </button>
                        <button onClick={() => handleDeleteNote(note.note_id)} className="hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Add Note */}
          <div className="border-t pt-2">
            <Textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Add a note..."
              className="text-xs min-h-[60px]"
            />
            <Button 
              size="sm" 
              className="mt-2 h-7 text-xs w-full" 
              onClick={handleAddNote} 
              disabled={saving || !newNoteContent.trim()}
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Add Note
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ═══════════════════════════════════════════════════════════════════
// INVOICE ROW MENU - Three-dot menu with Reprocess, Audit Trail
// ═══════════════════════════════════════════════════════════════════

const InvoiceRowMenu = ({ invoiceId, billId, accountId, onReprocess, onAuditTrail }) => {
  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      await onReprocess(invoiceId, billId);
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          <MoreVertical className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleReprocess} disabled={reprocessing}>
          {reprocessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Reprocess
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onAuditTrail}>
          <History className="h-4 w-4 mr-2" />
          Audit Trail
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TABLE COLUMN FILTER - Filter dropdown for each column
// ═══════════════════════════════════════════════════════════════════

const ColumnFilter = ({ column, values, selectedValues, onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Filter values based on search term
  const filteredValues = values.filter(v => 
    String(v).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (value) => {
    if (selectedValues.includes(value)) {
      onFilterChange(column, selectedValues.filter(v => v !== value));
    } else {
      onFilterChange(column, [...selectedValues, value]);
    }
  };

  const handleClear = () => {
    onFilterChange(column, []);
    setSearchTerm('');
  };

  const isFiltered = selectedValues.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className={`p-0.5 rounded transition-colors ${isFiltered ? 'text-blue-600 bg-blue-100' : 'text-muted-foreground hover:text-foreground'}`}
          title={isFiltered ? `${selectedValues.length} selected` : 'Filter'}
        >
          <Filter className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Search className="h-3 w-3 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="h-7 text-xs"
            />
          </div>
          
          {isFiltered && (
            <Button variant="ghost" size="sm" className="h-6 text-xs w-full" onClick={handleClear}>
              Clear ({selectedValues.length})
            </Button>
          )}
          
          <div className="max-h-48 overflow-y-auto space-y-1">
            {filteredValues.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No values</p>
            ) : (
              filteredValues.slice(0, 50).map((value, idx) => (
                <label key={idx} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(value)}
                    onChange={() => handleToggle(value)}
                    className="h-3 w-3"
                  />
                  <span className="truncate">{value || '(empty)'}</span>
                </label>
              ))
            )}
            {filteredValues.length > 50 && (
              <p className="text-xs text-muted-foreground text-center">...and {filteredValues.length - 50} more</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EditableCell = ({ value, field, rowId, isEditing, onEdit, type = 'number' }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  if (!isEditing) {
    return <span>{type === 'number' ? `$${Number(value || 0).toFixed(2)}` : value}</span>;
  }
  
  const handleChange = (e) => {
    const newVal = type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    setLocalValue(newVal);
    onEdit(rowId, field, newVal);
  };
  
  return (
    <Input
      type={type === 'number' ? 'number' : 'text'}
      step={type === 'number' ? '0.01' : undefined}
      value={localValue}
      onChange={handleChange}
      className="h-7 w-24 text-xs rounded-none"
    />
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const PaymentFileViewV2 = () => {
  const navigate = useNavigate();
  const { fileId: batchId } = useParams();
  const { user } = getAuth();
  const tableContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  
  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    checkAccount();
    const interval = setInterval(checkAccount, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [currentAccount?.id]);

  // State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [batchDetails, setBatchDetails] = useState(null);
  const [invoicePairs, setInvoicePairs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 50;
  
  // UI State
  const [showOriginal, setShowOriginal] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null); // Track which row is being edited
  
  // Payment modal
  const [paymentModal, setPaymentModal] = useState({ open: false, billId: null, amount: 0 });
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Editing state
  const [editedRows, setEditedRows] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  
  // Ops Approval state
  const [approvingInvoice, setApprovingInvoice] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [approvalStats, setApprovalStats] = useState({ total: 0, ops_approved: 0, ops_pending: 0 });
  
  // Bill Copy state - batched info to avoid N API calls
  const [billCopyInfo, setBillCopyInfo] = useState({}); // { billId: { hasCopy, filepath } }
  const [billCopyLoading, setBillCopyLoading] = useState(false);
  const [activeBillCopyId, setActiveBillCopyId] = useState(null); // Track which bill's PDF is open for row highlighting
  
  // Notes state - batched info for which invoices have notes
  const [notesInfo, setNotesInfo] = useState({}); // { invoiceId: noteCount }
  
  // Filter state
  const [columnFilters, setColumnFilters] = useState({
    account_no: [],
    supplier: [],
    validation_status: [],
    commodity: [],
    gl_code: []
  });
  
  // Reprocess state
  const [reprocessing, setReprocessing] = useState(false);
  
  // Audit Trail Sheet state (for three-dot menu)
  const [auditSheetOpen, setAuditSheetOpen] = useState(false);
  const [auditInvoiceId, setAuditInvoiceId] = useState(null);
  const [auditRowId, setAuditRowId] = useState(null);
  
  // Funds Received state
  const [fundsReceivedDialogOpen, setFundsReceivedDialogOpen] = useState(false);
  const [fundsReceivedEntries, setFundsReceivedEntries] = useState([]);
  const [fundsReceivedLoading, setFundsReceivedLoading] = useState(false);
  const [totalFundsReceived, setTotalFundsReceived] = useState(0);
  
  // Org Users for payment selector
  const [orgUsers, setOrgUsers] = useState([]);
  
  // Payment Stats
  const [paymentStats, setPaymentStats] = useState({
    total_paid: 0,
    paid_count: 0,
    paid_invoice_count: 0,
    unpaid_invoice_count: 0
  });
  
  // Paid/Unpaid filter and due date sort
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all', 'paid', 'unpaid'
  const [sortByDueDate, setSortByDueDate] = useState(true); // Default sort by due date
  
  // Unlock payments state
  const [unlocking, setUnlocking] = useState(false);
  
  // Permission check - Super Admin can edit
  const canEdit = user?.is_super_admin || currentAccount?.role === 'admin';
  const isSuperAdmin = user?.is_super_admin;
  
  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);
  
  // Calculate unique values for filters from loaded data
  const filterValues = useMemo(() => {
    const values = {
      account_no: new Set(),
      supplier: new Set(),
      validation_status: new Set(),
      commodity: new Set(),
      gl_code: new Set()
    };
    
    invoicePairs.forEach(pair => {
      const calc = pair?.calculated;
      if (!calc) return;
      if (calc.account_no) values.account_no.add(calc.account_no);
      if (calc.supplier) values.supplier.add(calc.supplier);
      if (calc.validation_status) values.validation_status.add(calc.validation_status);
      if (calc.commodity) values.commodity.add(calc.commodity);
      if (calc.gl_code) values.gl_code.add(calc.gl_code);
    });
    
    return {
      account_no: Array.from(values.account_no).sort(),
      supplier: Array.from(values.supplier).sort(),
      validation_status: Array.from(values.validation_status).sort(),
      commodity: Array.from(values.commodity).sort(),
      gl_code: Array.from(values.gl_code).sort()
    };
  }, [invoicePairs]);
  
  // Apply filters to invoice pairs (including paid/unpaid and due date sort)
  const filteredInvoicePairs = useMemo(() => {
    let filtered = invoicePairs.filter(pair => {
      const calc = pair?.calculated;
      if (!calc) return false;
      
      // Check paid/unpaid filter
      if (paymentStatusFilter === 'paid' && !calc.is_paid && calc.engine_payment_status !== 'Paid') return false;
      if (paymentStatusFilter === 'unpaid' && (calc.is_paid || calc.engine_payment_status === 'Paid')) return false;
      
      // Check each active column filter
      for (const [column, selectedValues] of Object.entries(columnFilters)) {
        if (selectedValues.length > 0) {
          const cellValue = calc[column] || '';
          if (!selectedValues.includes(cellValue)) {
            return false;
          }
        }
      }
      
      return true;
    });
    
    // Sort by due date if enabled (earliest first)
    if (sortByDueDate) {
      filtered = [...filtered].sort((a, b) => {
        const dateA = a.calculated?.due_date || a.original?.due_date || '';
        const dateB = b.calculated?.due_date || b.original?.due_date || '';
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      });
    }
    
    return filtered;
  }, [invoicePairs, columnFilters, paymentStatusFilter, sortByDueDate]);
  
  // Handle filter change
  const handleFilterChange = (column, values) => {
    setColumnFilters(prev => ({ ...prev, [column]: values }));
  };
  
  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some(arr => arr.length > 0);
  
  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({
      account_no: [],
      supplier: [],
      validation_status: [],
      commodity: [],
      gl_code: []
    });
  };

  // Fetch initial data
  const fetchData = useCallback(async (resetPairs = true) => {
    if (!currentAccount?.id || !batchId) return;
    
    if (resetPairs) setLoading(true);
    try {
      const [batchRes, invoicesRes, statsRes, notesRes, paymentStatsRes, fundsReceivedRes] = await Promise.all([
        api.get(`/payment-verification/v2/batches/${batchId}?account_id=${currentAccount.id}`),
        api.get(`/payment-verification/v2/batches/${batchId}/invoice-pairs?account_id=${currentAccount.id}&page=1&page_size=${pageSize}`),
        api.get(`/payment-verification/v2/batches/${batchId}/approval-stats?account_id=${currentAccount.id}`),
        api.get(`/payment-verification/v2/batches/${batchId}/notes?account_id=${currentAccount.id}`),
        api.get(`/payment-verification/v2/batches/${batchId}/payment-stats?account_id=${currentAccount.id}`),
        api.get(`/payment-verification/v2/batches/${batchId}/funds-received?account_id=${currentAccount.id}`)
      ]);
      
      setBatchDetails(batchRes.data.batch);
      if (resetPairs) {
        setInvoicePairs(invoicesRes.data.pairs || []);
        setPage(1);
      }
      setTotalCount(invoicesRes.data.total || 0);
      setHasMore((invoicesRes.data.pairs || []).length === pageSize);
      setApprovalStats(statsRes.data || { total: 0, ops_approved: 0, ops_pending: 0 });
      setNotesInfo(notesRes.data.notes_map || {});
      setPaymentStats(paymentStatsRes.data || { total_paid: 0, paid_count: 0, paid_invoice_count: 0, unpaid_invoice_count: 0 });
      setFundsReceivedEntries(fundsReceivedRes.data.entries || []);
      setTotalFundsReceived(fundsReceivedRes.data.total_received || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load payment file');
      navigate('/payment-verification');
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id, batchId, navigate, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);
  
  // Fetch org users for payment selector
  useEffect(() => {
    const fetchOrgUsers = async () => {
      if (!currentAccount?.id) return;
      try {
        const res = await api.get(`/payment-verification/v2/org-users?account_id=${currentAccount.id}`);
        setOrgUsers(res.data.users || []);
      } catch (error) {
        console.error('Error fetching org users:', error);
      }
    };
    fetchOrgUsers();
  }, [currentAccount?.id]);

  // Batch fetch bill copy info when invoice pairs change
  const fetchBillCopyInfoBatch = useCallback(async (pairs) => {
    if (!currentAccount?.id || !pairs || pairs.length === 0) return;
    
    // Get unique bill IDs from calculated rows
    const billIds = pairs
      .map(p => p.calculated?.bill_id)
      .filter(id => id && !billCopyInfo[id]); // Only fetch new ones
    
    if (billIds.length === 0) return;
    
    setBillCopyLoading(true);
    try {
      const response = await api.post(
        `/payment-verification/v2/bills/copy-info-batch?account_id=${currentAccount.id}`,
        billIds
      );
      
      const filepaths = response.data.filepaths || {};
      
      // Update state with new bill copy info
      const newInfo = {};
      billIds.forEach(billId => {
        newInfo[billId] = {
          hasCopy: !!filepaths[billId],
          filepath: filepaths[billId] || null
        };
      });
      
      setBillCopyInfo(prev => ({ ...prev, ...newInfo }));
    } catch (error) {
      console.error('Error fetching bill copy info batch:', error);
    } finally {
      setBillCopyLoading(false);
    }
  }, [currentAccount?.id, billCopyInfo]);

  // Fetch bill copy info when invoice pairs are loaded
  useEffect(() => {
    if (invoicePairs.length > 0) {
      fetchBillCopyInfoBatch(invoicePairs);
    }
  }, [invoicePairs, fetchBillCopyInfoBatch]);

  // Lazy loading - fetch more data
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !currentAccount?.id) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await api.get(
        `/payment-verification/v2/batches/${batchId}/invoice-pairs?account_id=${currentAccount.id}&page=${nextPage}&page_size=${pageSize}`
      );
      
      const newPairs = response.data.pairs || [];
      if (newPairs.length > 0) {
        setInvoicePairs(prev => [...prev, ...newPairs]);
        setPage(nextPage);
      }
      setHasMore(newPairs.length === pageSize);
    } catch (error) {
      console.error('Error loading more:', error);
      toast.error('Failed to load more rows');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, batchId, currentAccount?.id, pageSize]);

  // Go to specific page (for pagination buttons)
  const goToPage = useCallback(async (targetPage) => {
    if (targetPage < 1 || targetPage > totalPages || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const response = await api.get(
        `/payment-verification/v2/batches/${batchId}/invoice-pairs?account_id=${currentAccount.id}&page=${targetPage}&page_size=${pageSize}`
      );
      
      const newPairs = response.data.pairs || [];
      setInvoicePairs(newPairs);
      setPage(targetPage);
      setHasMore(newPairs.length === pageSize && targetPage < totalPages);
      
      // Scroll to top of table
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error('Error navigating to page:', error);
      toast.error('Failed to load page');
    } finally {
      setLoadingMore(false);
    }
  }, [batchId, currentAccount?.id, pageSize, totalPages, loadingMore]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const trigger = loadMoreTriggerRef.current;
    if (trigger) {
      observer.observe(trigger);
    }

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `$${Number(amount).toFixed(2)}`;
  };

  // Handle cell edit
  const handleCellEdit = (rowId, field, value) => {
    setEditedRows(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value }
    }));
  };

  // Save row changes
  const handleSaveRow = async (rowId, invoiceId) => {
    if (!editedRows[rowId]) return;
    
    setSavingRow(rowId);
    try {
      await api.patch(
        `/payment-verification/v2/invoices/${invoiceId}?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        {
          ...editedRows[rowId],
          comment: editedRows[rowId]?.comment || ''
        }
      );
      toast.success('Changes saved');
      setEditedRows(prev => {
        const newState = { ...prev };
        delete newState[rowId];
        return newState;
      });
      fetchData(false);
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSavingRow(null);
    }
  };

  // Handle payment
  const handleRecordPayment = async (paymentData) => {
    setPaymentLoading(true);
    try {
      await api.post(
        `/payment-verification/v2/batches/${batchId}/payments?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        paymentData
      );
      toast.success('Payment recorded successfully');
      setPaymentModal({ open: false, billId: null, amount: 0 });
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle download with mode
  const handleDownload = async (mode = 'screen') => {
    try {
      const response = await api.get(
        `/payment-verification/v2/batches/${batchId}/download?account_id=${currentAccount.id}&mode=${mode}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = mode === 'screen' ? '_screen' : '_full_corrected';
      link.setAttribute('download', batchDetails?.filename?.replace('.csv', `${suffix}.csv`) || `payment_file${suffix}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Download started (${mode === 'screen' ? 'Screen Data' : 'Full Data'})`);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  // Handle download original CSV from Engine S3
  const handleDownloadOriginalCsv = async () => {
    try {
      toast.info('Fetching original CSV link...');
      const { data } = await api.get(`/payment-files-engine/batch/${batchId}/csv-url`);
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success('Download started');
      } else if (data.s3_key) {
        // For uploaded files, use the existing download
        handleDownload('full');
      } else {
        toast.error('No original CSV available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Original CSV not available for this batch');
    }
  };

  // Handle reprocess operations
  const handleReprocessAll = async () => {
    setReprocessing(true);
    try {
      await api.post(
        `/payment-verification/v2/batches/${batchId}/reprocess?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`
      );
      toast.success('Reprocessing started. Please wait...');
      // Poll for completion
      setTimeout(() => fetchData(true), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start reprocessing');
    } finally {
      setReprocessing(false);
    }
  };

  const handleReprocessErrors = async () => {
    setReprocessing(true);
    try {
      const response = await api.post(
        `/payment-verification/v2/batches/${batchId}/reprocess-errors?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`
      );
      if (response.data.error_count === 0) {
        toast.info('No error invoices to reprocess');
      } else {
        toast.success(`Reprocessing ${response.data.error_count} error invoices...`);
        setTimeout(() => fetchData(true), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reprocess errors');
    } finally {
      setReprocessing(false);
    }
  };

  const handleReprocessSingle = async (invoiceId, billId) => {
    try {
      await api.post(
        `/payment-verification/v2/invoices/${invoiceId}/reprocess?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`
      );
      toast.success('Reprocessing invoice...');
      setTimeout(() => fetchData(false), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reprocess invoice');
    }
  };

  // Handle adding funds received
  const handleAddFundsReceived = async (formData) => {
    setFundsReceivedLoading(true);
    try {
      await api.post(
        `/payment-verification/v2/batches/${batchId}/funds-received?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        formData
      );
      toast.success('Funds received entry added');
      setFundsReceivedDialogOpen(false);
      // Refresh data to get updated totals
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add funds received');
    } finally {
      setFundsReceivedLoading(false);
    }
  };
  
  // Handle unlocking payments (Super Admin only)
  const handleUnlockPayments = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can unlock payments');
      return;
    }
    
    setUnlocking(true);
    try {
      const response = await api.post(
        `/payment-verification/v2/batches/${batchId}/unlock-payments?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&is_super_admin=true`
      );
      toast.success(response.data.message || 'Payments unlocked');
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock payments');
    } finally {
      setUnlocking(false);
    }
  };

  // Handle note changes (to update UI indicator)
  const handleNoteChange = (invoiceId, hasNote) => {
    setNotesInfo(prev => ({
      ...prev,
      [invoiceId]: hasNote ? (prev[invoiceId] || 0) + 1 : Math.max(0, (prev[invoiceId] || 1) - 1)
    }));
  };

  // Handle opening audit trail from three-dot menu
  const handleOpenAuditTrail = (invoiceId, rowId) => {
    setAuditInvoiceId(invoiceId);
    setAuditRowId(rowId);
    setAuditSheetOpen(true);
  };

  // Handle single invoice ops approval
  const handleOpsApprove = async (invoiceId, approve) => {
    setApprovingInvoice(invoiceId);
    try {
      await api.post(
        `/payment-verification/v2/batches/${batchId}/ops-approve?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        {
          invoice_ids: [invoiceId],
          action: approve ? 'approve' : 'unapprove'
        }
      );
      
      // Update local state immediately for responsiveness
      setInvoicePairs(prev => prev.map(pair => {
        if (pair.calculated?.invoice_id === invoiceId) {
          return {
            ...pair,
            calculated: {
              ...pair.calculated,
              is_ops_approved: approve,
              ops_approved_by_name: approve ? user.name : null,
              ops_approved_at: approve ? new Date().toISOString() : null
            }
          };
        }
        return pair;
      }));
      
      // Update approval stats
      setApprovalStats(prev => ({
        ...prev,
        ops_approved: approve ? prev.ops_approved + 1 : prev.ops_approved - 1,
        ops_pending: approve ? prev.ops_pending - 1 : prev.ops_pending + 1
      }));
      
      toast.success(approve ? 'Invoice approved' : 'Approval removed');
    } catch (error) {
      toast.error('Failed to update approval status');
    } finally {
      setApprovingInvoice(null);
    }
  };

  // Handle bulk ops approval
  const handleBulkOpsApprove = async (approve = true) => {
    setBulkApproving(true);
    try {
      const response = await api.post(
        `/payment-verification/v2/batches/${batchId}/ops-approve-all?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&action=${approve ? 'approve' : 'unapprove'}`
      );
      
      toast.success(response.data.message || `${approve ? 'Approved' : 'Unapproved'} all invoices`);
      
      // Refresh data to get updated approval states
      fetchData(false);
    } catch (error) {
      toast.error('Failed to bulk approve');
    } finally {
      setBulkApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Fixed Header */}
      <div className="border-b bg-card px-4 py-3 flex-shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/payment-verification')} className="flex-shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">{batchDetails?.filename}</h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span>{batchDetails?.total_rows} rows</span>
                <span>•</span>
                <span>{batchDetails?.uploaded_at && format(new Date(batchDetails.uploaded_at), 'MMM d, yyyy h:mm a')}</span>
                {batchDetails?.external_file_id && (
                  <>
                    <span>•</span>
                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                      Engine: {batchDetails.external_file_id.slice(0, 8)}...
                    </span>
                  </>
                )}
                {batchDetails?.source === 'engine_pull' && (
                  <Badge variant="outline" className="text-xs px-1 py-0">Engine Pull</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
            {/* Bulk Approve Button - Prominent at top */}
            {canEdit && (
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={approvalStats.ops_pending > 0 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleBulkOpsApprove(true)}
                        disabled={bulkApproving || approvalStats.ops_pending === 0}
                        data-testid="bulk-approve-button"
                      >
                        {bulkApproving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                        )}
                        Bulk Approve ({approvalStats.ops_pending})
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Approve all pending invoices for operational validation</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {approvalStats.ops_approved > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBulkOpsApprove(false)}
                    disabled={bulkApproving}
                    className="text-muted-foreground"
                  >
                    Reset
                  </Button>
                )}
              </div>
            )}
            
            {/* Show Original Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="show-original"
                checked={showOriginal}
                onCheckedChange={setShowOriginal}
              />
              <Label htmlFor="show-original" className="text-sm cursor-pointer flex items-center gap-1">
                {showOriginal ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                Show Original
              </Label>
            </div>
            <FAQInfoPopup />
            
            {/* Reprocess Dropdown with Both Options */}
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="reprocess-dropdown" disabled={reprocessing}>
                    {reprocessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Reprocess
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Reprocess Errors Only */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!batchDetails?.error_rows}>
                        <AlertCircle className="h-4 w-4 mr-2 text-amber-600" />
                        Reprocess Errors Only ({batchDetails?.error_rows || 0})
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reprocess Errors?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to reprocess {batchDetails?.error_rows || 0} error invoices?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleReprocessErrors} className="bg-amber-600 hover:bg-amber-700">
                          Yes, Reprocess
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  
                  {/* Reprocess All */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <RefreshCw className="h-4 w-4 mr-2 text-red-600" />
                        Reprocess All ({batchDetails?.total_rows || 0})
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-destructive">Reprocess All Invoices?</AlertDialogTitle>
                        <AlertDialogDescription>
                          <span className="text-destructive font-medium">Warning:</span> This will re-validate all {batchDetails?.total_rows || 0} invoices and overwrite any manual corrections.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleReprocessAll} className="bg-destructive hover:bg-destructive/90">
                          Yes, Reprocess All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="download-dropdown">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleDownload('screen')} data-testid="download-screen">
                  <Eye className="h-4 w-4 mr-2" />
                  Screen Data Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownload('full')} data-testid="download-full">
                  <Download className="h-4 w-4 mr-2" />
                  Full Data with Corrections
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDownloadOriginalCsv} data-testid="download-original-csv">
                  <FileText className="h-4 w-4 mr-2" />
                  Original Source CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* More... Menu - Top Right */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="more-menu-dropdown">
                  <MoreHorizontal className="h-4 w-4 mr-2" />
                  More...
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setFundsReceivedDialogOpen(true)} data-testid="add-funds-received">
                  <Banknote className="h-4 w-4 mr-2" />
                  Add Funds Received Amount
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => fetchData(false)} data-testid="refresh-data">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Funds Received Dialog */}
      <FundsReceivedDialog 
        batchId={batchId}
        onSubmit={handleAddFundsReceived}
        loading={fundsReceivedLoading}
        open={fundsReceivedDialogOpen}
        onOpenChange={setFundsReceivedDialogOpen}
      />

      {/* Stats Bar with Pagination - All stats are at FULL PAYMENT FILE level */}
      <div className="border-b bg-muted/30 px-6 py-3 flex-shrink-0">
        <div className="flex gap-6 items-center flex-wrap">
          {/* Refresh Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fetchData(false)}
                  className="h-8 w-8"
                  data-testid="refresh-stats"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Refresh stats & data</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <div>
            <span className="text-xs uppercase text-muted-foreground">Total</span>
            <p className="text-xl font-bold">{batchDetails?.total_rows}</p>
          </div>
          <div>
            <span className="text-xs uppercase text-muted-foreground">Validated</span>
            <p className="text-xl font-bold text-emerald-600">{batchDetails?.validated_rows}</p>
          </div>
          <div>
            <span className="text-xs uppercase text-muted-foreground">Corrected</span>
            <p className="text-xl font-bold text-amber-600">{batchDetails?.corrected_rows}</p>
          </div>
          <div>
            <span className="text-xs uppercase text-muted-foreground">Errors</span>
            <p className="text-xl font-bold text-red-600">{batchDetails?.error_rows}</p>
          </div>
          
          {/* Total Requested Amount - Corrected/calculated from invoices */}
          <div className="border-l pl-6 ml-2">
            <span className="text-xs uppercase text-muted-foreground">Total Funds Requested</span>
            <p className="text-xl font-bold text-blue-600">
              ${(batchDetails?.total_funds_requested || batchDetails?.total_original_requested || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          
          {/* Funds Received - shown only if > 0 */}
          {totalFundsReceived > 0 && (
            <div className="border-l pl-6 ml-2">
              <span className="text-xs uppercase text-muted-foreground">Funds Received</span>
              <p className="text-xl font-bold text-purple-600">
                ${totalFundsReceived.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          )}
          
          {/* Total Paid Amount */}
          <div className="border-l pl-6 ml-2">
            <span className="text-xs uppercase text-muted-foreground">Paid Amount</span>
            <p className="text-xl font-bold text-emerald-600">
              ${(paymentStats.total_paid || batchDetails?.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {(() => {
              const paidCount = paymentStats.paid_invoice_count || paymentStats.paid_count || batchDetails?.paid_invoice_count || 0;
              const totalCount = paidCount + (paymentStats.unpaid_invoice_count || 0);
              return paidCount > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {paidCount}{totalCount > 0 ? ` of ${totalCount}` : ''} paid
                </p>
              ) : null;
            })()}
          </div>
          
          {/* Approval Stats */}
          <div className="border-l pl-6 ml-2">
            <span className="text-xs uppercase text-muted-foreground">Ops Approved</span>
            <p className="text-xl font-bold text-emerald-600">
              {approvalStats.ops_approved}/{approvalStats.total}
            </p>
          </div>
          
          {/* Filter indicator */}
          {hasActiveFilters && (
            <div className="border-l pl-6 ml-2">
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-blue-600 hover:text-blue-700">
                <X className="h-3 w-3 mr-1" />
                Clear Filters ({filteredInvoicePairs.length}/{invoicePairs.length})
              </Button>
            </div>
          )}
          
          {/* Paid/Unpaid Filter and Due Date Sort */}
          <div className="ml-auto flex items-center gap-4">
            {/* Payment Status Filter */}
            <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bills</SelectItem>
                <SelectItem value="paid">Paid Only</SelectItem>
                <SelectItem value="unpaid">Unpaid Only</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Due Date Sort Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={sortByDueDate ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSortByDueDate(!sortByDueDate)}
                    data-testid="sort-due-date"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-1" />
                    Due Date
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{sortByDueDate ? 'Sorted by earliest due date' : 'Click to sort by due date'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loadingMore}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <span className="text-sm font-medium px-2">
                Page {page} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loadingMore}
                data-testid="pagination-next"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}
            </div>
          </div>
        </div>
      </div>

      {/* Table Container - auto height with vertical scroll, sticky headers */}
      <div 
        ref={tableContainerRef}
        className="flex-1 overflow-auto"
      >
        <div className="min-w-[3200px]">
          <table className="w-full caption-bottom text-sm border-collapse">
            <thead>
              <tr className="border-b-2">
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-12 text-center sticky top-0 bg-card z-10">#</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-10 sticky top-0 bg-card z-10">Type</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-72 sticky top-0 bg-card z-10">Bill ID</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-32 sticky top-0 bg-card z-10">
                  <div className="flex items-center gap-1">
                    Account
                    <ColumnFilter 
                      column="account_no" 
                      values={filterValues.account_no} 
                      selectedValues={columnFilters.account_no}
                      onFilterChange={handleFilterChange}
                    />
                  </div>
                </th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-48 sticky top-0 bg-card z-10">Property</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-36 sticky top-0 bg-card z-10">
                  <div className="flex items-center gap-1">
                    Supplier
                    <ColumnFilter 
                      column="supplier" 
                      values={filterValues.supplier} 
                      selectedValues={columnFilters.supplier}
                      onFilterChange={handleFilterChange}
                    />
                  </div>
                </th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Issue Date</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Due Date</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-24 sticky top-0 bg-card z-10">
                  <div className="flex items-center gap-1">
                    Commodity
                    <ColumnFilter 
                      column="commodity" 
                      values={filterValues.commodity} 
                      selectedValues={columnFilters.commodity}
                      onFilterChange={handleFilterChange}
                    />
                  </div>
                </th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-24 sticky top-0 bg-card z-10">
                  <div className="flex items-center gap-1">
                    GL Code
                    <ColumnFilter 
                      column="gl_code" 
                      values={filterValues.gl_code} 
                      selectedValues={columnFilters.gl_code}
                      onFilterChange={handleFilterChange}
                    />
                  </div>
                </th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Usage Charge</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">New Charges</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-24 sticky top-0 bg-card z-10">Late Fee</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">BBF</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Total Due</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-32 sticky top-0 bg-card z-10">Funds Calc</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-36 sticky top-0 bg-card z-10">Total Requested</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Paid Amount</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-32 sticky top-0 bg-card z-10">Payment Status</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-28 sticky top-0 bg-card z-10">Method</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-32 sticky top-0 bg-card z-10">Actions</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-10 sticky top-0 bg-card z-10">PDF</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-10 sticky top-0 bg-card z-10">Notes</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-12 sticky top-0 bg-card z-10">✓</th>
              </tr>
            </thead>
            <TableBody>
              {(() => {
                const seenBillIds = new Set();
                return filteredInvoicePairs.map((pair) => {
                const calc = pair?.calculated;
                if (!calc) return null; // Skip pairs with no calculated data
                const orig = pair?.original;
                const hasEdits = editedRows[calc.row_number];
                const isCorrected = calc.status === 'corrected' || calc.validation_status === 'corrected';
                const isFirstOccurrence = !seenBillIds.has(calc.bill_id);
                if (calc.bill_id) seenBillIds.add(calc.bill_id);
                
                return (
                  <React.Fragment key={`pair-${calc.row_number}`}>
                    {/* Original Row (conditionally shown) */}
                    {showOriginal && orig && (
                      <TableRow className="bg-slate-50 dark:bg-slate-900/50">
                        <TableCell className="text-center text-muted-foreground">{calc.row_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs bg-slate-100">O</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{orig.bill_id || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{orig.account_no || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground truncate max-w-[180px]">{orig.property_name || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{orig.supplier || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{orig.issue_date || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{orig.due_date || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{orig.commodity || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">{orig.gl_code || '-'}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.usage_charges)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.new_charges)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.late_fee)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.previous_balance)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.total_amount_due)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">-</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(orig.total_requested_amount)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">-</TableCell>
                        <TableCell className="text-center text-muted-foreground">-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    )}
                    
                    {/* Calculated Row */}
                    <TableRow 
                      className={`hover:bg-muted/30 ${isCorrected ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''} ${activeBillCopyId === calc.bill_id ? 'bg-blue-100 dark:bg-blue-950/30 ring-2 ring-blue-400 ring-inset' : ''}`}
                    >
                      {/* Row Number with Corrected Badge */}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {isCorrected && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="h-5 w-5 p-0 flex items-center justify-center bg-amber-100 text-amber-700 border-amber-300">
                                    C
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Corrected Invoice</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <span className="text-muted-foreground">{calc.row_number}</span>
                        </div>
                      </TableCell>
                      
                      {/* Type Badge - V for Validated, E for Error */}
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  calc.validation_status === 'error' || calc.validation_status === 'not_found'
                                    ? 'bg-red-100 text-red-700 border-red-300' 
                                    : calc.db_bill_found 
                                      ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                                      : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {calc.validation_status === 'error' || calc.validation_status === 'not_found' ? 'E' : calc.db_bill_found ? 'V' : 'N'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {calc.validation_status === 'error' || calc.validation_status === 'not_found' ? (
                                <div className="space-y-1">
                                  <p className="font-semibold text-red-600">Error</p>
                                  <p className="text-xs">{calc.error_message || calc.validation_message || 'Record not found in database'}</p>
                                </div>
                              ) : (
                                <p>{calc.db_bill_found ? 'Verified against DB' : 'Not found in DB'}</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      
                      {/* Bill ID */}
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          {calc.total_funds_requested && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <DollarSign className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent><p>Primary row for BillId aggregation</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <span className="truncate" title={calc.bill_id}>{calc.bill_id}</span>
                        </div>
                      </TableCell>
                      
                      {/* Account with Master Badge */}
                      <TableCell className="text-xs">
                        <MasterAccountBadge 
                          accountNo={calc.account_no}
                          isChildAccount={calc.is_child_account}
                          masterAccountNo={calc.master_account_no}
                          isConsolidatedBill={calc.is_consolidated_bill}
                          rowMarker={calc.row_marker}
                          dbSubCurrentBillAmount={calc.db_sub_current_bill_amount}
                        />
                      </TableCell>
                      
                      {/* Property */}
                      <TableCell className="text-xs truncate max-w-[180px]" title={calc.property_name}>
                        {calc.property_name || '-'}
                      </TableCell>
                      
                      {/* Supplier */}
                      <TableCell className="text-xs truncate max-w-[140px]" title={calc.supplier}>
                        {calc.supplier || '-'}
                      </TableCell>
                      
                      {/* Issue Date */}
                      <TableCell className="text-xs whitespace-nowrap">{calc.issue_date || '-'}</TableCell>
                      
                      {/* Due Date */}
                      <TableCell className="text-xs whitespace-nowrap">{calc.due_date || '-'}</TableCell>
                      
                      {/* Commodity */}
                      <TableCell className="text-xs">{calc.commodity || '-'}</TableCell>
                      
                      {/* GL Code */}
                      <TableCell className="text-xs font-mono">{calc.gl_code || '-'}</TableCell>
                      
                      {/* Usage Charge - For synthetic master, show direct value */}
                      <TableCell className="text-right">
                        {calc.is_synthetic_master ? (
                          <span className="text-emerald-700">{calc.usage_charges ? `$${calc.usage_charges.toFixed(2)}` : '-'}</span>
                        ) : editingRowId === calc.row_number ? (
                          <EditableCell
                            value={editedRows[calc.row_number]?.usage_charges ?? calc.usage_charges}
                            field="usage_charges"
                            rowId={calc.row_number}
                            isEditing={true}
                            onEdit={handleCellEdit}
                          />
                        ) : (
                          <FieldValue 
                            value={orig?.usage_charges}
                            dbValue={calc.db_usage_charges}
                            isMatch={calc.db_usage_charges === undefined || calc.usage_charges === calc.db_usage_charges}
                          />
                        )}
                      </TableCell>
                      
                      {/* New Charges - For synthetic master, show direct value */}
                      <TableCell className="text-right">
                        {calc.is_synthetic_master ? (
                          <span className="text-emerald-700">{calc.new_charges ? `$${calc.new_charges.toFixed(2)}` : '-'}</span>
                        ) : editingRowId === calc.row_number ? (
                          <EditableCell
                            value={editedRows[calc.row_number]?.new_charges ?? calc.new_charges}
                            field="new_charges"
                            rowId={calc.row_number}
                            isEditing={true}
                            onEdit={handleCellEdit}
                          />
                        ) : (
                          <FieldValue 
                            value={orig?.new_charges}
                            dbValue={calc.db_new_charges}
                            isMatch={calc.db_new_charges === undefined || calc.new_charges === calc.db_new_charges}
                          />
                        )}
                      </TableCell>
                      
                      {/* Late Fee */}
                      <TableCell className="text-right">
                        {editingRowId === calc.row_number ? (
                          <EditableCell
                            value={editedRows[calc.row_number]?.late_fee ?? calc.late_fee}
                            field="late_fee"
                            rowId={calc.row_number}
                            isEditing={true}
                            onEdit={handleCellEdit}
                          />
                        ) : (
                          <FieldValue 
                            value={orig?.late_fee}
                            dbValue={calc.db_late_fee}
                            isMatch={calc.db_late_fee === undefined || calc.late_fee === calc.db_late_fee}
                          />
                        )}
                      </TableCell>
                      
                      {/* BBF - N/A for child accounts in consolidated bills, direct value for synthetic MA */}
                      <TableCell className="text-right">
                        {calc.is_child_account && calc.is_consolidated_bill ? (
                          <span className="text-muted-foreground">$0.00</span>
                        ) : calc.is_synthetic_master ? (
                          // For synthetic master rows, show the calculated BBF directly (no original to compare)
                          <span className="text-emerald-700">{calc.previous_balance ? `$${calc.previous_balance.toFixed(2)}` : '$0.00'}</span>
                        ) : editingRowId === calc.row_number ? (
                          <EditableCell
                            value={editedRows[calc.row_number]?.previous_balance ?? calc.previous_balance}
                            field="previous_balance"
                            rowId={calc.row_number}
                            isEditing={true}
                            onEdit={handleCellEdit}
                          />
                        ) : (
                          <FieldValue 
                            value={orig?.previous_balance}
                            dbValue={calc.db_previous_balance}
                            isMatch={calc.db_previous_balance === undefined || calc.previous_balance === calc.db_previous_balance}
                          />
                        )}
                      </TableCell>
                      
                      {/* Total Due - N/A for child accounts in consolidated bills, direct value for synthetic MA */}
                      <TableCell className="text-right">
                        {calc.is_child_account && calc.is_consolidated_bill ? (
                          <span className="text-muted-foreground">$0.00</span>
                        ) : calc.is_synthetic_master ? (
                          // For synthetic master rows, show the calculated Total Due directly
                          <span className="text-emerald-700">{calc.total_amount_due ? `$${calc.total_amount_due.toFixed(2)}` : '$0.00'}</span>
                        ) : editingRowId === calc.row_number ? (
                          <EditableCell
                            value={editedRows[calc.row_number]?.total_amount_due ?? calc.total_amount_due}
                            field="total_amount_due"
                            rowId={calc.row_number}
                            isEditing={true}
                            onEdit={handleCellEdit}
                          />
                        ) : (
                          <FieldValue 
                            value={orig?.total_amount_due}
                            dbValue={calc.db_total_amount_due}
                            isMatch={calc.db_total_amount_due === undefined || calc.total_amount_due === calc.db_total_amount_due}
                          />
                        )}
                      </TableCell>
                      
                      {/* Funds Calculated */}
                      <TableCell className="text-right font-medium">
                        {formatCurrency(calc.funds_calculated)}
                      </TableCell>
                      
                      {/* Total Requested Amount */}
                      <TableCell className="text-right">
                        {calc.total_funds_requested ? (
                          <span className="font-bold text-emerald-700">
                            {formatCurrency(calc.total_funds_requested)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      
                      {/* Paid Amount - only show on first occurrence of bill_id */}
                      <TableCell className="text-right">
                        {!isFirstOccurrence ? (
                          <span className="text-muted-foreground text-xs italic">dup</span>
                        ) : (calc.is_paid || calc.engine_payment_status === 'Paid') ? (
                          <span className="font-medium text-emerald-700">
                            {formatCurrency(calc.paid_amount || calc.total_funds_requested || 0)}
                          </span>
                        ) : calc.paid_amount > 0 ? (
                          <span className="font-medium">{formatCurrency(calc.paid_amount)}</span>
                        ) : (
                          <span className="text-muted-foreground">$0.00</span>
                        )}
                      </TableCell>
                      
                      {/* Payment Status Column - only show on first occurrence */}
                      <TableCell className="text-center">
                        {!isFirstOccurrence ? (
                          <span className="text-muted-foreground text-xs italic">-</span>
                        ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Badge
                              variant="outline"
                              className={`cursor-pointer text-xs ${
                                (calc.is_paid || calc.engine_payment_status === 'Paid')
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200' 
                                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                              }`}
                              data-testid={`payment-badge-${calc.bill_id}`}
                            >
                              {(calc.is_paid || calc.engine_payment_status === 'Paid') ? 'PAID' : 'UNPAID'}
                            </Badge>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-0" align="end">
                            <PaymentPopoverContent
                              billId={calc.bill_id}
                              amount={calc.total_funds_requested}
                              isPaid={calc.is_paid || calc.engine_payment_status === 'Paid'}
                              paymentDetails={{
                                amount_paid: calc.paid_amount || calc.total_funds_requested,
                                payment_date: calc.transaction_date || calc.payment_date,
                                transaction_reference: calc.transaction_reference,
                                transaction_type: calc.transaction_type,
                                payment_type: calc.payment_type,
                                paid_by_name: calc.paid_by_name,
                                transaction_fee: calc.payment_service_charge || calc.transaction_fee,
                                engine_payment_status: calc.engine_payment_status,
                              }}
                              onSubmit={handleRecordPayment}
                              loading={paymentLoading}
                              orgUsers={orgUsers}
                              currentUser={user}
                              newCharges={calc.current_bill_amount}
                              totalAmountDue={calc.total_amount_due}
                            />
                          </PopoverContent>
                        </Popover>
                        )}
                      </TableCell>
                      
                      {/* Calculation Method Badge */}
                      <TableCell>
                        {calc.db_bill_found ? (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Badge
                                variant="outline"
                                className={`cursor-pointer hover:bg-primary/10 text-xs ${
                                  calc.bbf_included 
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300' 
                                    : 'bg-blue-50 text-blue-700 border-blue-300'
                                }`}
                              >
                                {calc.billing_sequence === 'FIRST_BILL' && '1ST'}
                                {calc.billing_sequence === 'SECOND_BILL' && '2ND'}
                                {calc.billing_sequence === 'THIRD_PLUS' && '3RD+'}
                                {calc.bbf_included ? ' +BBF' : ' BASE'}
                              </Badge>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <Database className="h-5 w-5" />
                                  Calculation Breakdown
                                </DialogTitle>
                                <DialogDescription>
                                  Row #{calc.row_number} • Bill: {calc.bill_id?.slice(0, 16)}...
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                                {/* Current Bill Details Section */}
                                <div className="border border-border rounded-lg p-3">
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Current Bill</h4>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-muted-foreground text-xs block">Vendor</span>
                                      <span className="font-medium">{calc.supplier || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground text-xs block">Account No</span>
                                      <div className="flex items-center gap-1">
                                        <MasterAccountBadge 
                                          accountNo={calc.account_no}
                                          isChildAccount={calc.is_child_account}
                                          masterAccountNo={calc.master_account_no}
                                          isConsolidatedBill={calc.is_consolidated_bill}
                                          rowMarker={calc.row_marker}
                                          dbSubCurrentBillAmount={calc.db_sub_current_bill_amount}
                                        />
                                      </div>
                                    </div>
                                    {/* Master Account Row - Only show if child account */}
                                    {calc.is_child_account && calc.master_account_no && (
                                      <>
                                        <div className="col-span-2 border-t pt-2 mt-1">
                                          <span className="text-muted-foreground text-xs block">Master Account</span>
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium font-mono text-xs text-blue-700">{calc.master_account_no}</span>
                                            <button
                                              onClick={async () => {
                                                await navigator.clipboard.writeText(calc.master_account_no);
                                                toast.success('Master account copied');
                                              }}
                                              className="p-1 hover:bg-muted rounded"
                                              title="Copy master account"
                                            >
                                              <Copy className="h-3 w-3 text-muted-foreground" />
                                            </button>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground text-xs block">Issue Date</span>
                                      <span className="font-medium">{calc.issue_date || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground text-xs block">Period</span>
                                      <span className="font-medium">{calc.period_from || '-'} to {calc.period_to || '-'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground text-xs block">New Charges</span>
                                      <span className="font-medium">${Number(calc.new_charges || 0).toFixed(2)}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground text-xs block">BBF (Previous Balance)</span>
                                      <span className="font-medium">${Number(calc.previous_balance || 0).toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Previous Bill Details Section */}
                                <div className="border border-border rounded-lg p-3">
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Previous Bill (Comparison)</h4>
                                  {calc.previous_bill_count > 0 ? (
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <span className="text-muted-foreground text-xs block">Vendor</span>
                                        <span className="font-medium">{calc.prev_supplier || calc.supplier || '-'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground text-xs block">Account No</span>
                                        <span className="font-medium font-mono text-xs">{calc.prev_account_no || calc.account_no || '-'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground text-xs block">Issue Date</span>
                                        <span className="font-medium">{calc.prev_issue_date || '-'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground text-xs block">Period</span>
                                        <span className="font-medium">{calc.prev_period_from || '-'} to {calc.prev_period_to || '-'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground text-xs block">New Charges</span>
                                        <span className="font-medium">{calc.prev_new_charges != null ? `$${Number(calc.prev_new_charges).toFixed(2)}` : '-'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground text-xs block">Due Date</span>
                                        <span className="font-medium">{calc.prev_due_date || '-'}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground italic">No previous bill found for this account</p>
                                  )}
                                </div>
                                
                                {/* Calculation Methodology Section */}
                                <div className="border border-border rounded-lg p-3 bg-muted/30">
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Calculation Methodology</h4>
                                  
                                  <div className="flex items-center gap-2 mb-3">
                                    <Badge className={
                                      calc.billing_sequence === 'FIRST_BILL' ? 'bg-purple-100 text-purple-800' : 
                                      calc.billing_sequence === 'SECOND_BILL' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                                    }>
                                      {calc.billing_sequence?.replace('_', ' ')}
                                    </Badge>
                                    {calc.is_child_account && (
                                      <Badge className="bg-blue-100 text-blue-800">
                                        CONSOLIDATED
                                      </Badge>
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                      ({calc.previous_bill_count || 0} previous bills found)
                                    </span>
                                  </div>
                                  
                                  {/* Step-by-step Calculation for Consolidated Bills */}
                                  {calc.is_consolidated_calculation && calc.calculation_steps && (
                                    <div className="mb-3 border rounded-lg overflow-hidden">
                                      <div className="bg-slate-100 px-3 py-2">
                                        <span className="text-xs font-semibold uppercase text-slate-600">Step-by-Step Calculation</span>
                                      </div>
                                      <div className="p-2 space-y-1">
                                        {calc.calculation_steps.map((step, idx) => (
                                          <div key={idx} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                                            <span className="text-muted-foreground">
                                              {step.step}. {step.description}
                                            </span>
                                            <div className="flex items-center gap-2">
                                              <span className={`font-mono ${step.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {step.value >= 0 ? '+' : ''}{step.value.toFixed(2)}
                                              </span>
                                              <span className="text-muted-foreground">→</span>
                                              <span className="font-mono font-semibold">${step.running_total.toFixed(2)}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Calculation Components Summary - NEW FLOW */}
                                  {calc.calculation_components && (
                                    <div className="mb-3">
                                      <div className="text-xs font-semibold text-muted-foreground mb-2">Calculation Flow:</div>
                                      
                                      {/* Step 1: Usage Charges from DB */}
                                      <div className="bg-blue-50 p-2 rounded mb-2">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-muted-foreground">1. Usage Charges (from DB)</span>
                                          <span className="font-mono font-semibold text-blue-700">${calc.calculation_components.usage_charges?.toFixed(2) || '0.00'}</span>
                                        </div>
                                      </div>
                                      
                                      {/* Step 2: New Charges = Usage + LateFee - Credits - Other Charges */}
                                      <div className="bg-emerald-50 p-2 rounded mb-2">
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="text-xs text-muted-foreground">2. New Charges = Usage + LateFee - Credits - Other Charges</span>
                                          <span className="font-mono font-semibold text-emerald-700">${calc.calculation_components.new_charges?.toFixed(2) || calc.new_charges?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground pl-2 border-l-2 border-emerald-200">
                                          <div className="flex justify-between">
                                            <span>+ Late Fee:</span>
                                            <span>${calc.calculation_components.late_fee?.toFixed(2) || '0.00'}</span>
                                          </div>
                                          <div className="flex justify-between text-red-600">
                                            <span>- Credits:</span>
                                            <span>-${calc.calculation_components.credits?.toFixed(2) || '0.00'}</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Step 3: Funds Calc = New Charges + BBF */}
                                      <div className={`p-2 rounded ${calc.bbf_included ? 'bg-amber-50' : 'bg-slate-50'}`}>
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="text-xs text-muted-foreground">3. Funds Calculated = New Charges + BBF</span>
                                          <span className="font-mono font-semibold">${calc.funds_calculated?.toFixed(2) || '0.00'}</span>
                                        </div>
                                        {calc.bbf_included ? (
                                          <div className="text-xs text-amber-700 pl-2 border-l-2 border-amber-200">
                                            + BBF: ${calc.calculation_components.bbf?.toFixed(2) || calc.previous_balance?.toFixed(2) || '0.00'}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-slate-500 pl-2 border-l-2 border-slate-200">
                                            BBF not included
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className={`p-3 rounded-lg border mb-3 ${calc.bbf_included ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                    <div className="flex items-center gap-2 font-medium mb-1">
                                      {calc.bbf_included ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-amber-600" />}
                                      <span>{calc.bbf_included ? 'BBF Included' : 'BBF Excluded'}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {calc.bbf_note || (
                                        calc.billing_sequence === 'FIRST_BILL' 
                                          ? 'First bill - no previous balance.'
                                          : calc.bbf_included
                                            ? `Gap of ${calc.gap_days || 0} days exceeds threshold (${calc.bbf_threshold || 25}d).`
                                            : calc.bbf_warning || 'BBF not included.'
                                      )}
                                    </p>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                                    <div className="p-2 bg-background rounded border">
                                      <span className="text-muted-foreground block text-xs">Gap Days</span>
                                      <span className="font-semibold">{calc.gap_days ?? '-'}</span>
                                      {calc.bbf_threshold && (
                                        <span className="text-xs text-muted-foreground ml-1">(threshold: {calc.bbf_threshold}d)</span>
                                      )}
                                    </div>
                                    <div className="p-2 bg-background rounded border">
                                      <span className="text-muted-foreground block text-xs">Due Crossed</span>
                                      <span className="font-semibold">{calc.crossed_due ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="p-3 bg-slate-100 rounded-lg">
                                    <span className="text-xs text-muted-foreground">Formula Applied</span>
                                    <code className="text-xs font-mono block mt-1 break-words">
                                      {calc.calculation_formula || calc.calculation_method || 'N/A'}
                                    </code>
                                  </div>
                                  
                                  {/* DB Comparison Note for Consolidated Bills */}
                                  {calc.is_child_account && calc.db_total_amount_due && (
                                    <div className={`mt-3 p-2 rounded-lg border ${
                                      Math.abs((calc.funds_calculated || 0) - calc.db_total_amount_due) < 0.01 
                                        ? 'bg-emerald-50 border-emerald-200' 
                                        : 'bg-amber-50 border-amber-200'
                                    }`}>
                                      <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">DB Total Amount Due:</span>
                                        <span className="font-mono font-semibold">${calc.db_total_amount_due?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex items-center justify-between text-xs mt-1">
                                        <span className="text-muted-foreground">Our Calculated Funds:</span>
                                        <span className="font-mono font-semibold">${calc.funds_calculated?.toFixed(2)}</span>
                                      </div>
                                      {Math.abs((calc.funds_calculated || 0) - calc.db_total_amount_due) >= 0.01 && (
                                        <div className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                                          <AlertTriangle className="h-3 w-3" />
                                          Difference: ${Math.abs((calc.funds_calculated || 0) - calc.db_total_amount_due).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-500 text-xs">N/A</Badge>
                        )}
                      </TableCell>
                      
                      {/* Actions */}
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {/* Edit Mode Controls */}
                          {editingRowId === calc.row_number ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 text-emerald-600"
                                onClick={() => {
                                  handleSaveRow(calc.row_number, calc.invoice_id);
                                  setEditingRowId(null);
                                }}
                                disabled={savingRow === calc.row_number}
                                title="Save changes"
                              >
                                {savingRow === calc.row_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 text-red-600"
                                onClick={() => {
                                  setEditingRowId(null);
                                  setEditedRows(prev => {
                                    const newState = { ...prev };
                                    delete newState[calc.row_number];
                                    return newState;
                                  });
                                }}
                                title="Cancel edit"
                              >
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Edit Button - Only for Admin */}
                              {canEdit && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 text-blue-600"
                                  onClick={() => setEditingRowId(calc.row_number)}
                                  title="Edit row"
                                  data-testid={`edit-row-${calc.row_number}`}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                              )}
                              {/* Save Button (shown when edits exist from other sources) */}
                              {hasEdits && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 text-emerald-600"
                                  onClick={() => handleSaveRow(calc.row_number, calc.invoice_id)}
                                  disabled={savingRow === calc.row_number}
                                >
                                  {savingRow === calc.row_number ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                </Button>
                              )}
                              {/* Three-dot menu */}
                              <InvoiceRowMenu
                                invoiceId={calc.invoice_id}
                                billId={calc.bill_id}
                                accountId={currentAccount?.id}
                                onReprocess={handleReprocessSingle}
                                onAuditTrail={() => handleOpenAuditTrail(calc.invoice_id, calc.row_number)}
                              />
                            </>
                          )}
                        </div>
                      </TableCell>
                      
                      {/* Bill Copy PDF - Moved to right */}
                      <TableCell className="text-center">
                        <BillCopyViewer 
                          billId={calc.bill_id} 
                          accountId={currentAccount?.id}
                          copyInfo={billCopyInfo[calc.bill_id]}
                          onOpenChange={setActiveBillCopyId}
                        />
                      </TableCell>
                      
                      {/* Notes */}
                      <TableCell className="text-center">
                        <InvoiceNotePopover
                          invoiceId={calc.invoice_id}
                          accountId={currentAccount?.id}
                          hasNote={!!notesInfo[calc.invoice_id]}
                          noteCount={notesInfo[calc.invoice_id]}
                          user={user}
                          onNoteChange={handleNoteChange}
                        />
                      </TableCell>
                      
                      {/* Operational Approval Checkmark */}
                      <TableCell className="text-center">
                        <OpsApprovalCheckmark
                          invoiceId={calc.invoice_id}
                          isOpsApproved={calc.is_ops_approved}
                          isCustomerApproved={calc.is_customer_approved}
                          onApprove={handleOpsApprove}
                          loading={approvingInvoice === calc.invoice_id}
                          opsApprovedByName={calc.ops_approved_by_name}
                          opsApprovedAt={calc.ops_approved_at}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              });
              })()}
            </TableBody>
          </table>
          
          {/* Lazy Load Trigger */}
          <div 
            ref={loadMoreTriggerRef} 
            className="h-20 flex items-center justify-center"
          >
            {loadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading more...</span>
              </div>
            )}
            {!hasMore && invoicePairs.length > 0 && (
              <span className="text-sm text-muted-foreground">
                All {invoicePairs.length} rows loaded
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Standalone Audit Trail Sheet (triggered from three-dot menu) */}
      <Sheet open={auditSheetOpen} onOpenChange={setAuditSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Audit Trail</SheetTitle>
            <SheetDescription>Row #{auditRowId} change history</SheetDescription>
          </SheetHeader>
          <AuditTrailContent invoiceId={auditInvoiceId} accountId={currentAccount?.id} />
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Standalone audit trail content component
const AuditTrailContent = ({ invoiceId, accountId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const fetchHistory = async () => {
      if (!invoiceId || !accountId) return;
      setLoading(true);
      try {
        const response = await api.get(`/payment-verification/v2/invoices/${invoiceId}/history?account_id=${accountId}`);
        setHistory(response.data.history || []);
      } catch (e) {
        console.error('Failed to fetch audit history:', e);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [invoiceId, accountId]);
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  
  if (history.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No changes recorded</p>;
  }
  
  return (
    <div className="mt-6 space-y-4 max-h-[70vh] overflow-y-auto">
      {history.map((entry, i) => (
        <div key={entry.audit_id || i} className="border-l-2 border-primary pl-4 py-2">
          <p className="text-xs text-muted-foreground">
            {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a') : '-'}
          </p>
          <p className="text-sm font-medium">
            {entry.action === 'update' ? 'Updated' : entry.action} by {entry.user_name || 'Unknown'}
          </p>
          <p className="text-sm">
            <span className="text-muted-foreground">{entry.field}:</span>{' '}
            <span className="line-through text-red-500">{entry.old_value || '-'}</span>{' → '}
            <span className="text-emerald-600 font-medium">{entry.new_value || '-'}</span>
          </p>
          {entry.comment && (
            <p className="text-xs text-muted-foreground mt-1 italic">"{entry.comment}"</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default PaymentFileViewV2;
